//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Microphone, SendFilled } from '@carbon/icons-react';
import { aiModuleStyles } from '../../../styles/LoopStyles';
import { cn } from '../../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import ToolSuggestions from './ToolSuggestions';
import SelectedTool from './SelectedTool';
import ContextWindowRing from './ContextWindowRing';
import StopButton from './StopButton';
import type { ToolSuggestion } from './AIModule';
import type { PlanApprovalRequest } from '../../../types/streaming';
import { isChromeBrowser } from '../../../utils/browserUtils';
import { useConfig } from '../../../Config';

export interface ChatInputAreaProps {
  darkMode: boolean;
  noLoopWarning: string;
  showClearCommand: boolean;
  showToolSuggestions: boolean;
  isLoadingTools: boolean;
  toolSuggestions: ToolSuggestion[];
  allToolSuggestions: ToolSuggestion[];
  selectedTool: string | null;
  contextWindow: {
    used: number;
    max: number;
    pct: number;
    model: string;
    estimated?: boolean;
  } | null;
  displayValue: string;
  inputMessage: string;
  isListening: boolean;
  isAIBuffering: boolean;
  isIngestionActive: boolean;
  isStopping: boolean;
  /** True while at least one tool_approval_request is pending. */
  hasHitlApproval: boolean;
  planApproval: PlanApprovalRequest | null;
  setInputMessage: (v: string) => void;
  setShowClearCommand: (v: boolean) => void;
  setShowToolSuggestions: (v: boolean) => void;
  setToolInputIndex: (v: number) => void;
  setToolSuggestions: (v: ToolSuggestion[]) => void;
  setSelectedTool: (v: string | null) => void;
  setNoLoopWarning: (v: string) => void;
  fetchToolsFromAPI: () => void;
  handleSendMessageWithTool: () => void;
  handleClearCommand: () => void;
  handleToolSelection: (tool: ToolSuggestion) => void;
  handleVoiceInput: () => void;
  onAbortAndStop: () => void;
}

const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  darkMode,
  noLoopWarning,
  showClearCommand,
  showToolSuggestions,
  isLoadingTools,
  toolSuggestions,
  allToolSuggestions,
  selectedTool,
  contextWindow,
  displayValue,
  inputMessage,
  isListening,
  isAIBuffering,
  isIngestionActive,
  isStopping,
  hasHitlApproval,
  planApproval,
  setInputMessage,
  setShowClearCommand,
  setShowToolSuggestions,
  setToolInputIndex,
  setToolSuggestions,
  setSelectedTool,
  setNoLoopWarning,
  fetchToolsFromAPI,
  handleSendMessageWithTool,
  handleClearCommand,
  handleToolSelection,
  handleVoiceInput,
  onAbortAndStop,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // A pending plan or tool approval must be resolved (Approve/Reject/Request
  // Changes) before another message can be sent on this conversation — the
  // backend treats any new chat_request as abandoning the pending HITL
  // interrupt and silently voids it (orchestration/cancel_repair.py), which
  // let both flows race and double-submit the same plan (NEXUS00007216).
  const isPlanOrHitlPending = hasHitlApproval || planApproval !== null;

  // Voice input relies on the Web Speech API, which only Chrome implements
  // reliably end-to-end — hide the mic entirely on other browsers rather
  // than showing a control that errors out on click. Also gated behind a
  // runtime feature flag (services.voiceInput.show, default on) so it can
  // be toggled off even in Chrome without a code change.
  const { services } = useConfig();
  const showMicButton =
    isChromeBrowser() && (services?.voiceInput?.show ?? true);

  // Return focus to the text input once it becomes typeable again — right
  // after mount, and whenever the AI finishes buffering a response — so the
  // user isn't forced to click back into it before typing the next message.
  useEffect(() => {
    if (!isAIBuffering) {
      inputRef.current?.focus();
    }
  }, [isAIBuffering]);

  // Auto-grow the textarea with content, up to the max-h cap in its
  // className (150px, ~6 lines) — plain <textarea> doesn't do this natively.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [displayValue]);

  return (
    <div
      id="id-ai-module-input-section"
      data-testid="ai-module-input-section"
      style={{
        ...aiModuleStyles.fixedContainerWithPosition,
        position: 'relative',
        width: '100%',
        backgroundColor: darkMode ? '#1e1e1e' : '#fff',
        borderTop: darkMode ? '1px solid #3f3f3f' : '1px solid #e5e7eb',
      }}
    >
      {noLoopWarning && (
        <div
          id="id-ai-warning"
          data-testid="id-ai-warning"
          style={aiModuleStyles.warningAI}
          role="alert"
          aria-live="assertive"
        >
          {noLoopWarning}
        </div>
      )}

      {showClearCommand && (
        <div
          id="id-ai-module-clear-suggestion"
          data-testid="ai-module-clear-suggestion"
          style={aiModuleStyles.clearCommandSuggestion}
        >
          <div
            id="id-ai-module-clear-command-item"
            data-testid="ai-module-clear-command-item"
            role="button"
            tabIndex={0}
            style={aiModuleStyles.clearCommandItem}
            onClick={() => {
              setInputMessage('');
              setShowClearCommand(false);
              handleClearCommand();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setInputMessage('');
                setShowClearCommand(false);
                handleClearCommand();
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#E3F2FD';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span
              id="id-ai-module-clear-command-text"
              data-testid="ai-module-clear-command-text"
              style={aiModuleStyles.clearCommandText}
            >
              /clear
            </span>
            <span
              id="id-ai-module-clear-command-label"
              data-testid="ai-module-clear-command-label"
            >
              {t('clear-conversation-history')}
            </span>
          </div>
        </div>
      )}
      <ToolSuggestions
        isVisible={showToolSuggestions}
        isLoading={isLoadingTools}
        tools={toolSuggestions}
        onSelectTool={handleToolSelection}
      />
      <div
        data-testid="id-ai-module-input-container"
        id="id-ai-module-input-container"
        style={aiModuleStyles.inputContainerWithPosition}
      >
        {selectedTool && (
          <div
            id="id-ai-module-selected-tool-wrapper"
            data-testid="ai-module-selected-tool-wrapper"
            style={{ width: '100%', marginBottom: '4px' }}
          >
            <SelectedTool
              toolName={selectedTool || ''}
              onRemove={() => {
                setInputMessage('');
                setSelectedTool(null);
              }}
            />
          </div>
        )}
        <div
          id="id-ai-module-input-row"
          data-testid="ai-module-input-row"
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'flex-end',
            gap: '8px',
          }}
        >
          {contextWindow && <ContextWindowRing data={contextWindow} />}
          {/** Prevent sending while AI is processing using isAIBuffering or isAIProcessing */}
          <div className="relative min-w-0 flex-1">
            {(() => {
              const textarea = (
                <textarea
                  ref={inputRef}
                  data-testid="id-ai-text-input"
                  id="id-ai-text-input"
                  aria-label={t('type-a-message', {
                    defaultValue: 'Type a message...',
                  })}
                  rows={1}
                  disabled={
                    isAIBuffering ||
                    isIngestionActive ||
                    isListening ||
                    isPlanOrHitlPending
                  }
                  placeholder={t('type-a-message', {
                    defaultValue: 'Type a message...',
                  })}
                  value={displayValue}
                  className={cn(
                    'block max-h-[150px] min-h-[44px] w-full resize-none overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words rounded-[22px] border border-[#e6e6e6] bg-white px-3 py-2.5 pr-12 font-[Roboto] text-sm text-[#12191F] shadow-none outline-none transition-all duration-300 [-ms-overflow-style:none] [scrollbar-width:none] placeholder:font-[Roboto] placeholder:text-sm placeholder:italic placeholder:text-[#3D5466] focus:border-2 focus:border-[#3C91FF] disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-scrollbar]:hidden',
                    darkMode &&
                      'border-[#4a4a4a] bg-[#2d2d2d] text-[#e0e0e0] hover:border-[#5a5a5a] focus:border-[#0066B0]',
                  )}
                  onChange={(e) => {
                    if (
                      isAIBuffering ||
                      isIngestionActive ||
                      isPlanOrHitlPending
                    ) {
                      // Block typing while a query is in progress, MCP tool
                      // ingestion is active, or a plan/tool approval is
                      // pending on this conversation.
                      return;
                    }
                    const value = e.target.value;
                    setInputMessage(value);

                    if (value.startsWith('/') && !value.includes(' ')) {
                      const partial = value.slice(1);
                      const target = 'clear';
                      if (partial === '' || target.startsWith(partial)) {
                        setShowClearCommand(true);
                        setShowToolSuggestions(false);
                        return;
                      } else {
                        setShowClearCommand(false);
                      }
                    } else {
                      setShowClearCommand(false);
                    }

                    // Tool hashtag suggestions — only when '#' is the very
                    // first character. A '#' anywhere else is regular text.
                    if (value.startsWith('#')) {
                      const textAfterHash = value.slice(1);
                      const hasSpace = textAfterHash.includes(' ');

                      if (hasSpace) {
                        // User finished typing the tool name (hit space) — close
                        setShowToolSuggestions(false);
                      } else {
                        setToolInputIndex(0);
                        setShowClearCommand(false);
                        setShowToolSuggestions(true);

                        if (textAfterHash === '') {
                          // Bare '#' — always fetch fresh from backend
                          fetchToolsFromAPI();
                        } else {
                          // Filter client-side against the full list from the
                          // last fetch (never against the already-filtered
                          // toolSuggestions — backspace would lose items).
                          setToolSuggestions(
                            allToolSuggestions.filter((tool) =>
                              tool.name
                                .toLowerCase()
                                .includes(textAfterHash.toLowerCase()),
                            ),
                          );
                        }
                      }
                    } else {
                      setShowClearCommand(false);
                      setShowToolSuggestions(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      isAIBuffering ||
                      isIngestionActive ||
                      isPlanOrHitlPending
                    ) {
                      // Block sending via keyboard while busy, or while a
                      // plan/tool approval is pending on this conversation.
                      e.preventDefault();
                      return;
                    }
                    if (e.key === 'Enter') {
                      // Allow Shift+Enter to insert a newline; Enter alone should send
                      if (e.shiftKey) {
                        return; // do not prevent default; let newline be added
                      }
                      e.preventDefault();
                      if (inputMessage.trim() === '/clear') {
                        handleClearCommand();
                        return;
                      }
                      if (showToolSuggestions && toolSuggestions.length > 0) {
                        // Select the first tool in the suggestion list
                        handleToolSelection(toolSuggestions[0]);
                      } else {
                        handleSendMessageWithTool();
                      }
                    } else if (
                      e.key === 'Tab' &&
                      showToolSuggestions &&
                      toolSuggestions.length > 0
                    ) {
                      // Allow Tab to select the first tool too
                      e.preventDefault(); // Prevent focus change
                      handleToolSelection(toolSuggestions[0]);
                    } else if (
                      e.key === 'ArrowDown' &&
                      showToolSuggestions &&
                      toolSuggestions.length > 0
                    ) {
                      // Focus the first tool suggestion when ArrowDown is pressed.
                      // Rows are keyed by tool name (not index), and
                      // getElementById avoids having to escape that name for
                      // use in a CSS selector.
                      const element = document.getElementById(
                        `id-tool-suggestion-${toolSuggestions[0].name}`,
                      );
                      if (element) {
                        (element as HTMLElement).focus();
                        e.preventDefault();
                      }
                    } else if (e.key === 'Escape') {
                      setShowToolSuggestions(false);
                    }
                  }}
                />
              );

              return isAIBuffering ||
                isIngestionActive ||
                isPlanOrHitlPending ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>{textarea}</TooltipTrigger>
                    <TooltipContent>
                      {isIngestionActive
                        ? t('ingestion-wait-to-type')
                        : isPlanOrHitlPending
                          ? t('plan-approval-wait-to-type')
                          : t('processing-wait-to-type')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                textarea
              );
            })()}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
              {showMicButton && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        id="id-ai-module-mic-wrapper"
                        data-testid="ai-module-mic-wrapper"
                        className="pointer-events-auto relative flex items-center"
                      >
                        <button
                          type="button"
                          aria-label={t('mic')}
                          onClick={handleVoiceInput}
                          disabled={isAIBuffering || isIngestionActive}
                          className={cn(
                            'relative flex items-center justify-center rounded-full p-2 transition-colors disabled:opacity-50',
                            isListening
                              ? 'hover:bg-red-500/20'
                              : 'hover:bg-black/5',
                          )}
                        >
                          <Microphone
                            size={20}
                            style={{
                              color: isListening ? '#ff0000' : '#6b7280',
                              transition: 'color 0.3s ease',
                            }}
                          />
                          {isListening && (
                            <span
                              id="id-ai-module-listening-indicator"
                              data-testid="ai-module-listening-indicator"
                              className="absolute right-0.5 top-0.5 box-border size-3 animate-pulse rounded-full bg-red-600"
                            />
                          )}
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isAIBuffering
                        ? t('voice-input-disabled')
                        : isListening
                          ? t('stop-voice-input')
                          : t('voice-input')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {isAIBuffering || isPlanOrHitlPending ? (
            <StopButton
              visible
              stopping={isStopping}
              onClick={onAbortAndStop}
            />
          ) : (
            <button
              onClick={(e) => {
                if (isIngestionActive) {
                  (e.target as HTMLElement).blur();
                  return;
                }
                if (!inputMessage.trim() && !selectedTool) {
                  setNoLoopWarning(t('message-cannot-be-empty'));
                  (e.target as HTMLElement).blur();
                  return;
                }
                handleSendMessageWithTool();
                (e.target as HTMLElement).blur();
              }}
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-full bg-[#0F62FE] text-sm font-normal text-white transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:bg-[#0F62FE] hover:shadow-[0_4px_8px_rgba(0,0,0,0.15)] focus-visible:rounded-md focus-visible:shadow-[0_0_0_2px_#90caf9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1976d2] active:translate-y-0 active:bg-[#0353E9] active:shadow-[0_2px_4px_rgba(0,0,0,0.1)] active:duration-100 disabled:cursor-not-allowed disabled:bg-[#cbd5e0]',
                isIngestionActive || (!inputMessage.trim() && !selectedTool)
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer',
              )}
              id="id-ai-send-button"
              data-testid="id-ai-send-button"
              disabled={
                isIngestionActive || (!inputMessage.trim() && !selectedTool)
              }
              aria-disabled={
                isIngestionActive || (!inputMessage.trim() && !selectedTool)
              }
              aria-label={t('send')}
            >
              <SendFilled size={18} style={{ color: '#fff' }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInputArea;
